import {
  ActionMenu,
  DotMenuHorizontalIcon,
  BodyMedium,
  Box,
  HeadingSmall,
  cleanArray,
  theme,
  useToast,
} from '@arnold/common';
import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';
import { Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import { Loading } from '../../../components';
import { Card } from '../../../components/Common';
import { ButtonLink } from '../../../components/StyledComponents';
import {
  Features,
  OrganizationQuery,
  TopicGroupQuery,
  useCloneProcessStepMutation,
  useDeleteProcessStepMutation,
  useUpdateProcessStepMutation,
} from '../../../generated/hooks';
import { getTopicGroup } from '../../../graphql/queries';
import { getTopicGroupTranslationByLanguageId } from '../../../lib/common';
import ee from '../../../lib/eventEmitter';
import { getTimeStringForOffset, prepareStepOffsets } from '../../../lib/process';
import { TopicGroupItemEditableContent } from './TopicGroupItemEditableContent';
import { TopicGroupItemStaticContent } from './TopicGroupItemStaticContent';

type Props = {
  step: NonNullable<TopicGroupQuery['topicGroup']['steps']>[0];
  organization: NonNullable<OrganizationQuery['organization']>;
  topicId?: string;
  topicGroupId: string;
  isEditable: boolean;
};

// 1 character width in pixels - approximately
const oneCharWidth = 7.7;
const getMaxCharByElementWidth = (width: number, maxRows: number) => {
  return Math.floor(width / oneCharWidth) * maxRows;
};

/** `editingStep` identifies which process step is open for editing (disambiguates clones sharing a topic). */
function urlSelectsThisStep(search: string, stepId: string): boolean {
  return new URLSearchParams(search).get('editingStep') === stepId;
}

function hasUrlStepEdit(search: string): boolean {
  return Boolean(new URLSearchParams(search).get('editingStep'));
}

export const TopicGroupStep = ({ step, organization, topicId, topicGroupId, isEditable }: Props) => {
  const location = useLocation();
  const [isEditing, setIsEditing] = useState(() => urlSelectsThisStep(location.search, step.id));
  const { t } = useTranslation('processDetail');
  const languageId = organization.primaryLanguageId.toString(10);
  const translation = getTopicGroupTranslationByLanguageId(languageId, step.stepTopicGroup.translations);
  const [saveProcessStep, { loading }] = useUpdateProcessStepMutation();
  const [deleteProcessStep] = useDeleteProcessStepMutation();
  const [cloneProcessStep, { loading: cloneLoading }] = useCloneProcessStepMutation();
  const { addToast } = useToast();
  const history = useHistory();
  const offsets = prepareStepOffsets({
    startOffsetMins: step.startOffsetMins,
    endOffsetMins: step.endOffsetMins,
    remindersOffsetMins: step.reminders.map((reminder) => reminder.reminderOffsetMins),
  });
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const descriptionRef = useRef<HTMLDivElement | null>(null);
  const [maxDescriptionChars, setMaxDescriptionChars] = useState<number | null>(null);
  const descriptionText = translation?.description || '';
  const isDescriptionTruncatable =
    !!descriptionText && maxDescriptionChars !== null && descriptionText.length > maxDescriptionChars;
  const collapsedChars = Math.max(0, (maxDescriptionChars ?? 0) - 10);
  const collapsedText = isDescriptionTruncatable ? `${descriptionText.slice(0, collapsedChars).trimEnd()}...` : '';
  const displayedDescriptionText = isDescriptionExpanded || !isDescriptionTruncatable ? descriptionText : collapsedText;

  useEffect(() => {
    if (urlSelectsThisStep(location.search, step.id)) {
      setIsEditing(true);
      const stepId = step.id;
      const runScroll = () => {
        const el = document.getElementById(`process-step-${stepId}`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el?.focus({ preventScroll: true });
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(runScroll);
      });
    } else if (hasUrlStepEdit(location.search)) {
      setIsEditing(false);
    }
  }, [location.search, step.id]);

  useEffect(() => {
    const update = () => {
      const width = descriptionRef.current?.clientWidth ?? 0;
      if (width <= 0) return;
      setMaxDescriptionChars(getMaxCharByElementWidth(width, 2));
    };

    const raf = requestAnimationFrame(update);
    window.addEventListener('resize', update);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
    };
  }, [descriptionText]);

  useEffect(() => {
    if (!descriptionText) {
      setIsDescriptionExpanded(false);
      return;
    }
    if (maxDescriptionChars !== null && descriptionText.length <= maxDescriptionChars) {
      setIsDescriptionExpanded(false);
    }
  }, [descriptionText, maxDescriptionChars]);

  const handleSave = (offsets: { startOffsetMins: number; remindersOffsetMins: number[]; endOffsetMins: number }) => {
    saveProcessStep({
      variables: {
        id: step.id,
        input: {
          startOffsetMins: offsets.startOffsetMins,
          remindersOffsetMins: offsets.remindersOffsetMins,
          endOffsetMins: offsets.endOffsetMins,
        },
      },
      onCompleted: () => {
        addToast(t('topicGroupStepCreated'));
        setIsEditing(false);
      },
      refetchQueries: [
        {
          query: getTopicGroup,
          variables: { topicGroupId },
        },
      ],
      awaitRefetchQueries: true,
    });
  };

  const openDeleteModal = () => {
    const startOffsetDays =
      offsets.startOffset?.days === undefined
        ? undefined
        : offsets.startOffset.days + (offsets.startOffset.days >= 0 ? 1 : 0);
    const startOffsetMins = getTimeStringForOffset(offsets.startOffset);
    ee.emitDeleteProcessStepModal(
      handleDelete,
      <BodyMedium margin={`0 0 ${theme.spacing.h} 0`}>
        {[translation?.value, t('processDetail:simpleDay', { dayNo: startOffsetDays }), startOffsetMins]
          .filter(Boolean)
          .join(', ')}
      </BodyMedium>,
    );
  };

  const handleDelete = async () => {
    await deleteProcessStep({
      variables: {
        id: step.id,
      },
      onCompleted: () => {
        addToast(t('topicGroupStepDeleted'));
      },
      refetchQueries: [{ query: getTopicGroup, variables: { topicGroupId } }],
    });
  };

  const handleClone = async () => {
    await cloneProcessStep({
      variables: { processStepId: step.id },
      onCompleted: (mutationData) => {
        addToast(t('topicGroupStepCreated'));
        const newStepId = mutationData.cloneProcessStep.id;
        if (newStepId) {
          const searchParams = new URLSearchParams(location.search);
          searchParams.set('editingStep', newStepId);
          history.push({
            pathname: location.pathname,
            search: searchParams.toString(),
          });
        }
      },
      refetchQueries: [
        {
          query: getTopicGroup,
          variables: { topicGroupId },
        },
      ],
      awaitRefetchQueries: true,
    });
  };

  const actionMenuItems = cleanArray([
    {
      label: t('topicDetail'),
      action: () => history.push(`/topicEditor/${step.stepTopicGroup.id}`),
    },
    isEditable && {
      label: t('copy'),
      action: handleClone,
    },
    isEditable && {
      label: t('remove'),
      action: openDeleteModal,
    },
  ]);

  // Insert edit topic menu item into second place if the org has the feature
  organization.featureNames?.includes(Features.CreateAndEditTopics) &&
    isEditable &&
    actionMenuItems.splice(1, 0, {
      label: t('editTopic'),
      action: () => history.push(`/topicEditor/0?topicId=${topicId}`),
    });

  return (
    <Container>
      {(loading || cloneLoading) && <Loading />}
      <Card id={`process-step-${step.id}`} tabIndex={-1}>
        <Header>
          <div style={{ maxWidth: '70%' }}>
            <HeadingSmall data-cy="topicGroupStep-title">{translation?.value}</HeadingSmall>
            <Description ref={descriptionRef} data-cy="topicGroupStep-description">
              {displayedDescriptionText}
              {isDescriptionTruncatable && (
                <DescriptionToggle
                  color={theme.colors.text.secondary}
                  onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                >
                  {isDescriptionExpanded ? t('showLess') : t('showMore')}
                </DescriptionToggle>
              )}
            </Description>
          </div>
          {!isEditing && (
            <ActionButtonsContainer>
              {isEditable && (
                <ButtonLink onClick={() => setIsEditing(true)} data-cy="topicGroupStep-editDate">
                  {t('editDataAndDate')}
                </ButtonLink>
              )}
              {!isEditable ? (
                <ButtonLink onClick={() => history.push(`/topic/${topicId}`)}>{t('topicDetail')}</ButtonLink>
              ) : (
                <DropdownContainer>
                  <ActionMenu
                    dataIcom="btn-reports-action-menu"
                    align="right"
                    toggleIcon={<DotMenuHorizontalIcon />}
                    items={actionMenuItems}
                  />
                </DropdownContainer>
              )}
            </ActionButtonsContainer>
          )}
        </Header>
      </Card>
      {isEditing ? (
        <TopicGroupItemEditableContent
          step={step}
          onSave={handleSave}
          onCancel={() => {
            setIsEditing(false);
            const params = new URLSearchParams(location.search);
            params.delete('editingStep');
            history.push({
              pathname: location.pathname,
              search: params.toString(),
            });
          }}
          loading={loading}
        />
      ) : (
        <TopicGroupItemStaticContent step={step} />
      )}
    </Container>
  );
};

const Container = styled(Box)`
  margin-bottom: ${theme.spacing.f};
  padding: 0;
  background-color: transparent;
`;

const ActionButtonsContainer = styled(Row)`
  align-items: center;
  gap: ${theme.spacing.g};
`;

const Header = styled(Row)`
  justify-content: space-between;
  flex-wrap: nowrap;
  margin-inline: ${theme.spacing.c};
  align-items: center;
`;

const Description = styled.div`
  color: ${theme.colors.text.secondary};
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const DescriptionToggle = styled.div`
  display: inline;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  margin-left: ${theme.spacing.b};
  font-size: ${theme.typography.body.medium.bold.fontSize};
  font-weight: ${theme.typography.body.medium.bold.fontWeight};
  line-height: ${theme.typography.body.medium.bold.lineHeight};
  color: ${theme.colors.text.secondary};
`;

const DropdownContainer = styled.span`
  display: inline-block;
  margin: 5px 0;
`;
